import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";
import { useTranslation } from "react-i18next";

const API_KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const INITIAL_PAGINATION = { current_page: 1, last_page: 1, per_page: 20, total: 0 };

const statusInfo = (value) => {
  const status = String(value ?? "").toLowerCase();
  if (status === "1" || status === "success") return { label: "Success", className: "success", icon: "fa-circle-check" };
  if (status === "0" || status === "failed") return { label: "Failed", className: "failed", icon: "fa-circle-xmark" };
  if (status === "2" || status === "cancel" || status === "cancelled") return { label: "Cancel", className: "cancel", icon: "fa-ban" };
  return { label: value === undefined || value === null || value === "" ? "Pending" : String(value), className: "pending", icon: "fa-clock" };
};

const TransactionHistory = () => {
  const { t } = useTranslation();
  const { CurrencySymbol } = GlobalSet();
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const requestId = useRef(0);

  const fetchTransactions = useCallback(async (page = 1) => {
    const authToken = localStorage.getItem("auth_token");
    if (!authToken) {
      setError("Please sign in to view your transactions.");
      setTransactions([]);
      return;
    }
    const id = ++requestId.current;
    setRequesting(true);
    setError("");
    try {
      const response = await axios.post(`/apps-transaction-history?page=${page}`, {
        key_s: API_KEY,
        currency: localStorage.getItem("selectedCurrency"),
      });
      if (id !== requestId.current) return;
      if (response?.data?.status === "success") {
        setTransactions(Array.isArray(response.data.data) ? response.data.data : []);
        const p = response.data.pagination || {};
        setPagination({
          current_page: Number(p.current_page) || page,
          last_page: Math.max(1, Number(p.last_page) || 1),
          per_page: Math.max(1, Number(p.per_page) || 20),
          total: Math.max(0, Number(p.total) || 0),
        });
      } else {
        setError(response?.data?.message || "Unable to retrieve transactions.");
        setTransactions([]);
      }
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err?.response?.data?.message || "Unable to connect. Please try again.");
    } finally {
      if (id === requestId.current) setRequesting(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions(1);
    return () => { requestId.current += 1; };
  }, [fetchTransactions]);

  const filtered = transactions.filter((row) => {
    const matchesStatus = filter === "all" || statusInfo(row.status).className === filter;
    const text = [row.amount, row.transction_Type, row.to_user_id_d, row.date, statusInfo(row.status).label].join(" ").toLowerCase();
    return matchesStatus && text.includes(search.trim().toLowerCase());
  });
  const pageNumbers = () => {
    const total = pagination.last_page;
    const start = Math.max(1, Math.min(pagination.current_page - 2, total - 4));
    return Array.from({ length: Math.min(5, total - start + 1) }, (_, i) => start + i);
  };
  const formatAmount = (amount) => {
    const n = Number(amount);
    return Number.isFinite(n) ? n.toLocaleString("en-US", { maximumFractionDigits: 8 }) : String(amount ?? "0");
  };
  const counts = { success: 0, failed: 0, pending: 0 };
  transactions.forEach((row) => {
    const type = statusInfo(row.status).className;
    if (type in counts) counts[type] += 1;
  });

  return (
    <div className="rmth-root">
      <style>{`
        body { background:#00191D !important; color:#fff !important; }
        .rmth-root { --cyan:#16B8C7; --navy:#00191D; --line:rgba(22,184,199,.19); min-height:100vh; background:#00191D; color:#fff; padding:18px 4px 42px; font-size:14px; }
        .rmth-root * { box-sizing:border-box; }
        .rmth-wrap { width:100%; max-width:1440px; margin:auto; }
        .rmth-head { display:flex; align-items:center; justify-content:space-between; gap:16px; padding:23px 24px; margin-bottom:14px; border:1px solid var(--line); border-radius:20px; background:linear-gradient(115deg,#07313a,#002229 62%,#00191D); animation:rmth-in .35s ease both; }
        .rmth-eyebrow { color:#55d8e2; font-size:11px; font-weight:800; letter-spacing:1.6px; text-transform:uppercase; }
        .rmth-head h2 { color:#fff; margin:6px 0; font-size:25px; font-weight:800; }
        .rmth-head p { color:#a0b7bd; margin:0; font-size:14px; }
        .rmth-head-icon { width:56px; height:56px; flex-shrink:0; display:grid; place-items:center; border:1px solid rgba(22,184,199,.35); border-radius:16px; background:rgba(22,184,199,.12); color:#59e4ef; font-size:24px; }
        .rmth-metrics { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; margin-bottom:14px; }
        .rmth-metric { min-width:0; border:1px solid var(--line); border-radius:16px; padding:17px; background:#05262c; transition:transform .2s,border-color .2s; }
        .rmth-metric:hover { transform:translateY(-2px); border-color:rgba(22,184,199,.55); }
        .rmth-metric.white { background:#f6fbfc; color:#09272d; border-color:#d9e8ea; }
        .rmth-metric-top { display:flex; justify-content:space-between; gap:8px; align-items:center; }
        .rmth-metric-label { font-size:13px; color:#9cb5b9; }
        .rmth-metric.white .rmth-metric-label { color:#526e74; }
        .rmth-metric i { color:#55d8e2; font-size:17px; }
        .rmth-metric.white i { color:#0796a5; }
        .rmth-metric strong { display:block; margin-top:10px; font-size:26px; font-weight:800; line-height:1.2; }
        .rmth-panel { overflow:hidden; background:#042329; border:1px solid var(--line); border-radius:20px; animation:rmth-in .4s ease both; }
        .rmth-toolbar { padding:18px; display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:12px; border-bottom:1px solid var(--line); }
        .rmth-toolbar-title h3 { color:#fff; font-size:18px; font-weight:800; margin:0 0 3px; }
        .rmth-toolbar-title p { color:#9ab3b8; font-size:12px; margin:0; }
        .rmth-controls { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .rmth-search { position:relative; min-width:190px; }
        .rmth-search i { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#66bac4; }
        .rmth-search input,.rmth-select { min-height:40px; border:1px solid rgba(22,184,199,.28); border-radius:10px; background:#00191D; color:#fff; outline:0; font-size:14px; }
        .rmth-search input { width:100%; padding:8px 12px 8px 36px; }
        .rmth-search input::placeholder { color:#77969c; }
        .rmth-select { padding:8px 12px; cursor:pointer; }
        .rmth-search input:focus,.rmth-select:focus { border-color:#16B8C7; box-shadow:0 0 0 3px rgba(22,184,199,.1); }
        .rmth-button { display:inline-flex; align-items:center; justify-content:center; gap:7px; min-height:40px; padding:9px 15px; border:0; border-radius:10px; background:linear-gradient(125deg,#16B8C7,#42d9e5); color:#00242a; font-size:14px; font-weight:800; cursor:pointer; transition:transform .2s,filter .2s; }
        .rmth-button:hover:not(:disabled) { transform:translateY(-2px); filter:brightness(1.08); }
        .rmth-button:disabled { opacity:.6; cursor:default; }
        .rmth-scroll { overflow-x:auto; }
        .rmth-table { width:100%; border-collapse:collapse; text-align:left; }
        .rmth-table th { background:#08343b; color:#b8e8ed; padding:15px 17px; font-size:12px; font-weight:800; white-space:nowrap; }
        .rmth-table td { padding:16px 17px; border-top:1px solid rgba(22,184,199,.12); color:#e4f2f4; font-size:14px; vertical-align:middle; }
        .rmth-table tbody tr { transition:background .18s; }
        .rmth-table tbody tr:hover { background:rgba(22,184,199,.06); }
        .rmth-amount { color:#6ce4ee; font-weight:800; white-space:nowrap; }
        .rmth-type { display:inline-block; padding:6px 10px; border:1px solid rgba(22,184,199,.25); border-radius:8px; color:#b3e5ea; background:#07333a; overflow-wrap:anywhere; }
        .rmth-user { overflow-wrap:anywhere; }
        .rmth-date { color:#a9c0c4; white-space:nowrap; }
        .rmth-status { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; font-size:12px; font-weight:800; white-space:nowrap; }
        .rmth-status.success { background:rgba(46,211,149,.12); color:#5ee4ae; }
        .rmth-status.failed { background:rgba(255,100,115,.12); color:#ff8994; }
        .rmth-status.cancel { background:rgba(190,205,211,.12); color:#c7d7db; }
        .rmth-status.pending { background:rgba(255,194,91,.12); color:#ffd48a; }
        .rmth-empty { padding:46px 18px; text-align:center; color:#a6bfc4; }
        .rmth-empty i { display:block; color:#47ccd8; font-size:29px; margin-bottom:12px; }
        .rmth-empty strong { display:block; color:#fff; margin-bottom:5px; }
        .rmth-mobile { display:none; }
        .rmth-footer { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; padding:16px 18px; border-top:1px solid var(--line); }
        .rmth-info { color:#9eb8bc; font-size:13px; }
        .rmth-pages { display:flex; align-items:center; flex-wrap:wrap; gap:6px; }
        .rmth-page { min-width:36px; min-height:36px; border-radius:9px; border:1px solid rgba(22,184,199,.28); background:#00191D; color:#fff; cursor:pointer; font-size:13px; transition:background .2s; }
        .rmth-page:hover:not(:disabled) { background:#0c3b42; }
        .rmth-page.active { border-color:#16B8C7; background:linear-gradient(125deg,#16B8C7,#42d9e5); color:#00242a; font-weight:800; }
        .rmth-page:disabled { opacity:.4; cursor:default; }
        .rmth-error { margin:12px 18px; padding:12px 14px; background:rgba(255,100,115,.09); border:1px solid rgba(255,100,115,.25); border-radius:10px; color:#ffb1b9; }
        @keyframes rmth-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @media(max-width:900px) { .rmth-metrics { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media(max-width:680px) {
          .rmth-root { padding-top:10px; }
          .rmth-head { padding:16px; border-radius:15px; }
          .rmth-head h2 { font-size:20px; }
          .rmth-head-icon { width:44px; height:44px; font-size:19px; }
          .rmth-metric { padding:13px; }
          .rmth-metric strong { font-size:22px; }
          .rmth-panel { border-radius:15px; }
          .rmth-toolbar { padding:14px; }
          .rmth-controls,.rmth-search { width:100%; }
          .rmth-select { flex:1; }
          .rmth-desktop { display:none; }
          .rmth-mobile { display:grid; gap:10px; padding:12px; }
          .rmth-entry { padding:15px; background:#00191D; border:1px solid rgba(22,184,199,.22); border-radius:13px; }
          .rmth-entry-top { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:14px; }
          .rmth-entry-top .rmth-amount { font-size:19px; }
          .rmth-entry-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
          .rmth-entry-grid small { display:block; margin-bottom:4px; color:#87a6ac; font-size:12px; }
          .rmth-entry-grid strong { display:block; color:#fff; font-size:13px; overflow-wrap:anywhere; font-weight:600; }
          .rmth-footer { justify-content:center; text-align:center; }
          .rmth-info { width:100%; }
        }
        @media(prefers-reduced-motion:reduce) { .rmth-root *, .rmth-root *::before, .rmth-root *::after { animation:none!important; transition:none!important; } }
      `}</style>
      <div className="rmth-wrap">
        <header className="rmth-head">
          <div>
            <span className="rmth-eyebrow">RM CROWN MARKET · Account activity</span>
            <h2><i className="fa-solid fa-clock-rotate-left" style={{ color: "#42d9e5", marginRight: 10 }} />{t("pro_TrHis_tr_his")}</h2>
            <p>View your transaction activity and payment status.</p>
          </div>
          <span className="rmth-head-icon"><i className="fa-solid fa-receipt" /></span>
        </header>

        <section className="rmth-metrics" aria-label="Transaction overview">
          <div className="rmth-metric white"><div className="rmth-metric-top"><span className="rmth-metric-label">Total Records</span><i className="fa-solid fa-layer-group" /></div><strong>{pagination.total.toLocaleString()}</strong></div>
          <div className="rmth-metric"><div className="rmth-metric-top"><span className="rmth-metric-label">Success on This Page</span><i className="fa-solid fa-circle-check" /></div><strong>{counts.success}</strong></div>
          <div className="rmth-metric"><div className="rmth-metric-top"><span className="rmth-metric-label">Pending on This Page</span><i className="fa-solid fa-clock" /></div><strong>{counts.pending}</strong></div>
          <div className="rmth-metric"><div className="rmth-metric-top"><span className="rmth-metric-label">Failed on This Page</span><i className="fa-solid fa-circle-xmark" /></div><strong>{counts.failed}</strong></div>
        </section>

        <section className="rmth-panel">
          <div className="rmth-toolbar">
            <div className="rmth-toolbar-title"><h3>Transaction Records</h3><p>Search and filter records on the current page</p></div>
            <div className="rmth-controls">
              <div className="rmth-search"><i className="fa-solid fa-magnifying-glass" /><input aria-label="Search current page transactions" placeholder="Search transactions..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
              <select className="rmth-select" aria-label="Filter by status" value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All statuses</option><option value="success">Success</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="cancel">Cancel</option>
              </select>
              <button type="button" className="rmth-button" onClick={() => fetchTransactions(pagination.current_page)} disabled={requesting}><i className="fa-solid fa-rotate-right" /> Refresh</button>
            </div>
          </div>
          {error && <div role="alert" className="rmth-error">{error}</div>}
          <div className="rmth-desktop rmth-scroll">
            <table className="rmth-table">
              <thead><tr><th>{t("pro_TrHis_amount")}</th><th>Type</th><th>To</th><th>{t("pro_TrHis_status")}</th><th>{t("pro_TrHis_date")}</th></tr></thead>
              <tbody>
                {filtered.map((row, index) => {
                  const s = statusInfo(row.status);
                  return <tr key={row.id ?? `${pagination.current_page}-${index}`}>
                    <td><span className="rmth-amount">{CurrencySymbol}{formatAmount(row.amount)}</span></td>
                    <td><span className="rmth-type">{row.transction_Type || "—"}</span></td>
                    <td><span className="rmth-user">{row.to_user_id_d || "—"}</span></td>
                    <td><span className={`rmth-status ${s.className}`}><i className={`fa-solid ${s.icon}`} />{s.label}</span></td>
                    <td><span className="rmth-date">{row.date || "—"}</span></td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
          <div className="rmth-mobile">
            {filtered.map((row, index) => {
              const s = statusInfo(row.status);
              return <article className="rmth-entry" key={row.id ?? `mobile-${pagination.current_page}-${index}`}>
                <div className="rmth-entry-top"><span className="rmth-amount">{CurrencySymbol}{formatAmount(row.amount)}</span><span className={`rmth-status ${s.className}`}><i className={`fa-solid ${s.icon}`} />{s.label}</span></div>
                <div className="rmth-entry-grid"><div><small>Type</small><strong>{row.transction_Type || "—"}</strong></div><div><small>To</small><strong>{row.to_user_id_d || "—"}</strong></div><div style={{ gridColumn: "1 / -1" }}><small>Date</small><strong>{row.date || "—"}</strong></div></div>
              </article>;
            })}
          </div>
          {filtered.length === 0 && !error && <div className="rmth-empty"><i className="fa-solid fa-folder-open" /><strong>No transactions found</strong><span>{search || filter !== "all" ? "Try a different search or status filter." : "Your transaction records will appear here."}</span></div>}
          <footer className="rmth-footer">
            <div className="rmth-info">Showing {pagination.total ? (pagination.current_page - 1) * pagination.per_page + 1 : 0}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} total records · {filtered.length} displayed</div>
            <nav className="rmth-pages" aria-label="Transaction pages">
              <button className="rmth-page" type="button" aria-label="Previous page" disabled={requesting || pagination.current_page <= 1} onClick={() => fetchTransactions(pagination.current_page - 1)}>‹</button>
              {pageNumbers().map((page) => <button key={page} type="button" aria-label={`Page ${page}`} aria-current={page === pagination.current_page ? "page" : undefined} className={`rmth-page ${page === pagination.current_page ? "active" : ""}`} disabled={requesting} onClick={() => page !== pagination.current_page && fetchTransactions(page)}>{page}</button>)}
              <button className="rmth-page" type="button" aria-label="Next page" disabled={requesting || pagination.current_page >= pagination.last_page} onClick={() => fetchTransactions(pagination.current_page + 1)}>›</button>
            </nav>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default TransactionHistory;
